import React, { useMemo } from 'react'

import { type MonthlyDistribution } from './faker/utils'
import { useLayerApiUrl } from './ImportDataContext'
import {
  type Bill,
  type BusinessIndustry,
  createCustomTransactions,
  createMatchingCustomTransactionsFromBills,
  type CustomAccount,
  getBills,
  ImportApiError,
  validateBills,
} from './importDummyDataHelpers'
import { ImportForm } from './ImportForm'
import { type ImportStepDef } from './useImportForm'

export interface ImportBillsProps {
  demoName: string
  businessId: string
  industry: BusinessIndustry
  businessAccessToken: string
  defaultCustomAccount: CustomAccount
  dist?: MonthlyDistribution
}

const buildBillSteps = (
  demoName: string,
  businessId: string,
  businessAccessToken: string,
  customAccountId: string,
  layerApiUrl: string,
) => (bills: Bill[]): ImportStepDef[] => [
  {
    label: 'Creating bills',
    run: async () => {
      const billResponse = await fetch(
        `${layerApiUrl}/v1/businesses/${businessId}/bills/bulk`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${businessAccessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bills),
        },
      )
      if (!billResponse.ok) {
        throw new ImportApiError(
          `Bill API call failed: ${billResponse.status} ${billResponse.statusText}`,
          billResponse.status,
        )
      }
    },
  },
  {
    label: 'Creating matching transactions',
    run: async () => {
      const matchingTxns = createMatchingCustomTransactionsFromBills(bills, demoName)
      await createCustomTransactions(
        businessId,
        businessAccessToken,
        customAccountId,
        matchingTxns,
        layerApiUrl,
      )
    },
  },
]

export const ImportBills = ({
  demoName,
  businessId,
  businessAccessToken,
  defaultCustomAccount,
  dist,
}: ImportBillsProps) => {
  const layerApiUrl = useLayerApiUrl()
  const getSteps = useMemo(
    () => buildBillSteps(demoName, businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl),
    [demoName, businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl],
  )

  return (
    <ImportForm
      title='Bills'
      defaultCustomAccount={defaultCustomAccount}
      getInitialData={() => getBills(demoName, dist)}
      validateData={validateBills}
      getSteps={getSteps}
      dependencies={[demoName, dist]}
    />
  )
}
